import { useState, useRef } from 'react'
import { motion } from 'motion/react'
import { Link } from '@tanstack/react-router'
import { EASE_OUT, BODY_FONT } from '@/lib/constants'
import { NEUTRAL1_RGB, NEUTRAL9_RGB } from '@/components/home/tokens'
import { canHover } from '@/lib/hooks'

const resetButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
}

// Panel/item paddings. The left-align offset is derived from these so the first
// option's text lines up with the trigger text regardless of tweaks.
const PANEL_PAD = 8
const ITEM_PAD_X = 14
const ITEM_PAD_Y = 10
const LEFT_ALIGN_OFFSET = -(PANEL_PAD + ITEM_PAD_X)

export type NavDropdownOption = {
  label: string
  to: string
}

function DropdownItem({
  label,
  to,
  accentColor,
  itemColor,
  onSelect,
}: {
  label: string
  to: string
  accentColor: string
  itemColor: string
  onSelect: () => void
}) {
  const [hovered, setHovered] = useState(false)
  return (
    // Real anchor (crawlable) with client-side nav; closes the menu on select.
    <Link
      to={to}
      onClick={onSelect}
      onMouseEnter={canHover ? () => setHovered(true) : undefined}
      onMouseLeave={canHover ? () => setHovered(false) : undefined}
      style={{
        ...resetButton,
        display: 'block',
        textDecoration: 'none',
        textAlign: 'left',
        padding: `${ITEM_PAD_Y}px ${ITEM_PAD_X}px`,
        borderRadius: 9,
        fontSize: 'var(--text-sm)',
        fontWeight: 500,
        fontFamily: BODY_FONT,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        color: hovered ? accentColor : itemColor,
        backgroundColor: hovered ? `rgba(${NEUTRAL1_RGB},0.06)` : 'rgba(255,255,255,0)',
        transition: 'color 0.2s ease, background-color 0.2s ease',
      }}
    >
      {label}
    </Link>
  )
}

/**
 * Reusable nav dropdown: a trigger label + animated caret that reveals a
 * glassy panel of options. Opens on hover (desktop) and toggles on click.
 *
 * Designed to drop into any menu — pass the menu's `accentColor` (hover
 * highlight) and optional `restColor` (trigger/label text at rest). `align`
 * controls whether the panel hangs left-aligned to the trigger text (default)
 * or centered under it.
 */
export function NavDropdown({
  label,
  options,
  accentColor,
  restColor,
  itemColor = '#F8F6F2',
  align = 'left',
  minWidth = 220,
}: {
  label: string
  options: NavDropdownOption[]
  accentColor: string
  restColor?: string
  /** Rest color for the option labels (defaults to warm cream). */
  itemColor?: string
  /** Panel alignment relative to the trigger. */
  align?: 'left' | 'center'
  minWidth?: number
}) {
  const [open, setOpen] = useState(false)
  const textColor = restColor ?? accentColor
  const closeTimer = useRef<number | undefined>(undefined)

  const openMenu = () => {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 140)
  }

  const panelPosition: React.CSSProperties =
    align === 'center'
      ? { left: '50%', transform: 'translateX(-50%)' }
      : { left: LEFT_ALIGN_OFFSET }

  return (
    <div
      onMouseEnter={canHover ? openMenu : undefined}
      onMouseLeave={canHover ? scheduleClose : undefined}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...resetButton, display: 'inline-flex', alignItems: 'center', gap: '5px' }}
      >
        <motion.span
          animate={{ color: textColor }}
          transition={{ duration: 1, ease: EASE_OUT }}
          style={{ fontSize: 'var(--text-sm)', fontWeight: 500, fontFamily: BODY_FONT }}
        >
          {label}
        </motion.span>
        <motion.span
          animate={{ color: textColor, rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT }}
          style={{ fontSize: '9px', lineHeight: 1, display: 'inline-block' }}
        >
          ▼
        </motion.span>
      </button>

      {/* Always mounted (open/close is opacity + visibility, not mount/unmount)
          so the option anchors exist in the DOM for crawlers — hover-revealed
          links are never discovered otherwise. `visibility` is a discrete value:
          motion flips it to hidden only after the close fade finishes, and it
          keeps the closed panel out of the tab order. */}
      <motion.div
        role="menu"
        aria-hidden={!open}
        initial={false}
        animate={
          open
            ? { opacity: 1, y: 0, visibility: 'visible' }
            : { opacity: 0, y: 8, visibility: 'hidden' }
        }
        transition={{ duration: 0.25, ease: EASE_OUT }}
        style={{
          position: 'absolute',
          top: 'calc(100% + 14px)',
          ...panelPosition,
          minWidth,
          display: 'flex',
          flexDirection: 'column',
          padding: PANEL_PAD,
          borderRadius: 14,
          // Frosted glass: translucent tint + blurred, saturated backdrop
          backgroundColor: `rgba(${NEUTRAL9_RGB},0.35)`,
          backdropFilter: 'blur(22px) saturate(160%)',
          WebkitBackdropFilter: 'blur(22px) saturate(160%)',
          boxShadow: `0 12px 40px rgba(${NEUTRAL9_RGB},0.35)`,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {options.map((opt) => (
          <DropdownItem
            key={opt.label}
            label={opt.label}
            to={opt.to}
            accentColor={accentColor}
            itemColor={itemColor}
            onSelect={() => setOpen(false)}
          />
        ))}
      </motion.div>
    </div>
  )
}

export default NavDropdown
